/**
 * Клиент API ReportFlow.
 *
 * Ключевые решения (ADR 0006):
 *  - access-токен живёт только в памяти (Zustand), не в localStorage:
 *    любой XSS иначе даёт долговременный доступ к аккаунту;
 *  - refresh-токен в HttpOnly cookie, JavaScript его не видит; обновление
 *    выполняется запросом с `credentials: include`;
 *  - при 401 выполняется одна попытка обновления сессии, после чего запрос
 *    повторяется. Параллельные 401 ждут одного общего обновления, иначе
 *    несколько одновременных запросов запустили бы ротацию несколько раз и
 *    сработала бы защита от повторного использования токена.
 */

import { useAuthStore } from '@/stores/auth'

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

/** Единый формат ошибки API (см. app/core/errors.py). */
export interface ApiErrorBody {
  error: {
    code: string
    message: string
    details: Record<string, unknown>
    request_id: string | null
  }
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: Record<string, unknown>
  readonly requestId: string | null

  constructor(status: number, body: ApiErrorBody['error']) {
    super(body.message)
    this.name = 'ApiError'
    this.status = status
    this.code = body.code
    this.details = body.details ?? {}
    this.requestId = body.request_id ?? null
  }

  /** Ошибки поля формы, если сервер их вернул. */
  get fieldErrors(): Record<string, string> {
    const fields: unknown = this.details.fields
    if (!Array.isArray(fields)) return {}

    const result: Record<string, string> = {}
    for (const item of fields as unknown[]) {
      if (typeof item !== 'object' || item === null) continue
      const record = item as Record<string, unknown>
      if (typeof record.field === 'string' && typeof record.reason === 'string') {
        result[record.field] = record.reason
      }
    }
    return result
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  /** Не подставлять заголовок активного пространства (эндпоинты уровня пользователя). */
  skipWorkspace?: boolean
  /** Не пытаться обновить сессию при 401 (используется самим обновлением). */
  skipRefresh?: boolean
  signal?: AbortSignal
}

/** Одно общее обновление сессии для всех параллельных запросов. */
let refreshInFlight: Promise<boolean> | null = null

async function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      })
      if (!response.ok) return false

      const data = (await response.json()) as { access_token: string; expires_at: string }
      useAuthStore.getState().setAccessToken(data.access_token)
      return true
    } catch {
      return false
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

async function parseError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as ApiErrorBody
    if (body?.error?.code) return new ApiError(response.status, body.error)
  } catch {
    // Тело не JSON — например, ошибка на уровне прокси
  }
  return new ApiError(response.status, {
    code: 'UNKNOWN_ERROR',
    message: 'Сервис недоступен. Попробуйте обновить страницу.',
    details: {},
    request_id: null,
  })
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, skipWorkspace = false, skipRefresh = false, signal } = options
  const state = useAuthStore.getState()

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (state.accessToken) headers.Authorization = `Bearer ${state.accessToken}`
  if (!skipWorkspace && state.activeWorkspaceId) {
    headers['X-Workspace-Id'] = state.activeWorkspaceId
  }

  const doFetch = (): Promise<Response> =>
    fetch(`${API_BASE}${path}`, {
      method,
      credentials: 'include',
      headers: {
        ...headers,
        // Токен мог обновиться, пока запрос стоял в очереди
        ...(useAuthStore.getState().accessToken
          ? { Authorization: `Bearer ${useAuthStore.getState().accessToken}` }
          : {}),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    })

  let response = await doFetch()

  if (response.status === 401 && !skipRefresh) {
    const refreshed = await refreshSession()
    if (refreshed) {
      response = await doFetch()
    } else {
      // Сессия окончательно недействительна — выходим, чтобы пользователь
      // увидел экран входа, а не бесконечные ошибки
      useAuthStore.getState().clearSession()
      throw await parseError(response)
    }
  }

  if (!response.ok) throw await parseError(response)
  if (response.status === 204) return undefined as T

  return (await response.json()) as T
}

/**
 * Загрузка файла через multipart-форму.
 *
 * Отдельная функция, а не `api.post`: с `FormData` нельзя ставить
 * `Content-Type` вручную — браузер сам добавляет `boundary`, и заданный
 * заголовок ломает разбор на сервере.
 *
 * `Idempotency-Key` обязателен для загрузки: без него двойной клик создаёт
 * две версии набора данных (ADR 0004).
 */
export async function uploadFile<T>(
  path: string,
  form: FormData,
  options: { idempotencyKey?: string } = {},
): Promise<T> {
  const state = useAuthStore.getState()

  const headers: Record<string, string> = {}
  if (state.accessToken) headers.Authorization = `Bearer ${state.accessToken}`
  if (state.activeWorkspaceId) headers['X-Workspace-Id'] = state.activeWorkspaceId
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey

  const send = (): Promise<Response> =>
    fetch(`${API_BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...headers,
        ...(useAuthStore.getState().accessToken
          ? { Authorization: `Bearer ${useAuthStore.getState().accessToken}` }
          : {}),
      },
      body: form,
    })

  let response = await send()

  if (response.status === 401) {
    const refreshed = await refreshSession()
    if (refreshed) {
      response = await send()
    } else {
      useAuthStore.getState().clearSession()
      throw await parseError(response)
    }
  }

  if (!response.ok) throw await parseError(response)
  return (await response.json()) as T
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
  refreshSession,
}
